import React, { useEffect, useState } from 'react';
import { PenLine, Lock, Users } from 'lucide-react';
import { StepShell, GuestTrackNote } from './ProgramStepCards';
import { ShareReflectionComposer } from './ShareReflectionComposer';

export const ProgramJournalStep = ({ day, progress, entry, locked, guest, onSave, onSkip, program }) => {
  const [share, setShare] = useState(false);
  const [text, setText] = useState(entry?.content || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setText(entry?.content || ''); setDirty(false); }, [entry?.id, entry?.updatedAt, day.dayNumber]);

  const done = !!progress?.journalComplete && !!entry;
  const skipped = !!progress?.journalSkipped && !done;

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await onSave(text.trim()); setDirty(false); } finally { setSaving(false); }
  };

  return (
    <StepShell icon={PenLine} label="Journal Reflection" done={done} testId="step-journal">
      <p className="text-base text-[#0f1e33] leading-snug mb-3 font-medium" data-testid="step-journal-prompt">{day.journalPrompt}</p>
      <p className="inline-flex items-center gap-1 text-[11px] text-[#7a9b8e] mb-3"><Lock className="w-3 h-3" /> Private — only you can see this.</p>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        disabled={locked && !guest}
        rows={4}
        placeholder="Write whatever comes…"
        className="w-full rounded-2xl border border-[#e3d2a8]/60 bg-white/70 p-3 text-sm text-[#0f1e33] placeholder:text-stone-400 focus:outline-none focus:border-[#D6AD60] disabled:opacity-60 resize-none"
        data-testid="step-journal-textarea"
      />
      {done && !dirty && <p className="text-xs font-semibold text-[#5c8477] mt-2" data-testid="step-journal-saved">✓ Saved to your Journal</p>}
      {entry && (
        <button type="button" onClick={() => setShare(true)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1e3a5f] hover:text-[#a88a4c] transition-colors" data-testid="step-journal-share">
          <Users className="w-3.5 h-3.5 text-[#D6AD60]" /> Share this reflection
        </button>
      )}
      <ShareReflectionComposer open={share} onClose={() => setShare(false)} text={entry?.content} circleId={day.circleIdResolved || program?.defaultCircleId}
        circleName={day.circleNameResolved || program?.defaultCircleName} prompt={day.circlePrompt} programSlug={program?.slug} programDayId={day.id} day={day.dayNumber}
        allowAnonymous={day.allowAnonymousCommunityPost !== false} />
      {skipped && !text && <p className="text-xs text-[#7a9b8e] mt-2" data-testid="step-journal-skipped">Skipped for today — write anytime and it will still count.</p>}
      {guest ? (
        <div className="mt-3"><GuestTrackNote testId="step-journal-guest-track" label="Sign in to save your reflection" /></div>
      ) : (
      <div className="grid grid-cols-2 gap-2 mt-3">
        {!done && !skipped ? (
          <button type="button" onClick={onSkip} disabled={locked} data-testid="step-journal-skip"
            className="h-11 rounded-full text-xs font-semibold uppercase tracking-[0.18em] text-[#7a9b8e] hover:text-[#1e3a5f] transition-colors disabled:opacity-50">Skip journal</button>
        ) : <span />}
        <button type="button" onClick={save} disabled={locked || saving || !text.trim() || (done && !dirty)} data-testid="step-journal-save"
          className="h-11 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.18em] hover:bg-[#1e3a5f] transition-colors disabled:opacity-50">
          {saving ? 'Saving…' : done ? 'Update' : 'Save'}
        </button>
      </div>
      )}
    </StepShell>
  );
};
